import React, { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StepPageProps } from '../../../assessment-portal';
import { useAssessmentPortalState } from '../../../assessment-portal';
import {
  ADHD_BASE,
  AdhdQuestionnaireFlowStep,
  QUESTIONNAIRE_TEMPLATE_ID,
  YOUNG_QUESTIONNAIRE_TEMPLATE_ID,
} from '../../data/constants';
import {
  hasBookingConfirmationSnapshot,
  loadBookingConfirmationSnapshot,
} from '../../utils/booking-confirmation-snapshot';
import { loadValidQuestionnaireDraft } from '../../utils/questionnaire-draft';
import { resolveInitialAgeRange } from '../../utils/questionnaire-variant';
import type { QuestionnaireVariant } from '../../utils/questionnaire-variant';
import type { AdhdQuestionnaireFlowState } from '../../types/flow';
import AgeRangePage from '../age-range-page';

const AgeRangeStep: FC<StepPageProps> = () => {
  const navigate = useNavigate();
  const {
    flowState,
    updateFlowState,
    goToStep,
  } = useAssessmentPortalState<AdhdQuestionnaireFlowState>();

  const initialRange = useMemo(() => resolveInitialAgeRange({
    adultDraft: loadValidQuestionnaireDraft(QUESTIONNAIRE_TEMPLATE_ID),
    youngDraft: loadValidQuestionnaireDraft(YOUNG_QUESTIONNAIRE_TEMPLATE_ID),
    dateOfBirth: loadBookingConfirmationSnapshot()?.dateOfBirth || '',
    today: new Date(),
  }), []);

  // Coming back from the intro keeps the choice already made.
  const [selected, setSelected] = useState<QuestionnaireVariant | null>(
    flowState.ageRange ?? (initialRange === 'under-min' ? null : initialRange),
  );
  // A wrong or stale booking date of birth must not block the user: they can choose themselves.
  const [isChoosingManually, setIsChoosingManually] = useState(false);
  const isUnderMinAge = !flowState.ageRange
    && initialRange === 'under-min'
    && !isChoosingManually;
  const canGoBackToBooked = hasBookingConfirmationSnapshot();

  const handleContinue = () => {
    if (!selected) return;
    if (flowState.ageRange && flowState.ageRange !== selected) {
      updateFlowState('questionnaireAnswers', {});
    }
    updateFlowState('ageRange', selected);
    goToStep(AdhdQuestionnaireFlowStep.ASSESSMENT_INTRO);
  };

  return (
    <AgeRangePage
      selected={selected}
      onSelect={setSelected}
      onContinue={handleContinue}
      isUnderMinAge={isUnderMinAge}
      onChooseManually={() => setIsChoosingManually(true)}
      showBack={canGoBackToBooked}
      onBack={canGoBackToBooked ? () => navigate(`${ADHD_BASE}/booked`) : undefined}
    />
  );
};

export default AgeRangeStep;
